import React from 'react';
import styled from 'styled-components';
import { Modal } from '../../../Components/uielements';
import { IntlMessages } from '../../../helper/langHelper';
import { PlayCircle } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { generalInfo } from '../../../services/bioplus-services/general';

// Styled Components
const SupportModalWrapper = styled.div`
  display: grid;
  gap: 10px;
  padding: 10px 20px 30px;
  min-width: 400px;
  max-height: 600px;
`;

const SupportSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #ffffff;
  margin: 0;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #b8b8b8;
`;

const ContactLabel = styled.span`
  color: #ffffff;
  min-width: 60px;
`;

const ContactLink = styled.a`
  color: #4a9eff;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin: 8px 0;
`;

const HelpfulLinksSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LinkContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const LinkLabel = styled.span`
  color: #4a9eff;
  font-size: 16px;
  text-decoration: underline;
  flex: 1;
`;

const ExternalLinkIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '↗';
    color: #4a9eff;
    font-size: 18px;
  }
`;

const LoadingText = styled.p`
  color: #b8b8b8;
  font-size: 14px;
  text-align: center;
  padding: 20px;
`;

const ErrorText = styled.p`
  color: #ff6b6b;
  font-size: 14px;
  text-align: center;
  padding: 20px;
`;

const SupportModal = ({ onClose }) => {
  const { data, isLoading, isError, error } = useQuery(['generalInfo'], () => generalInfo(), {
    staleTime: 24 * 60 * 60 * 1000,
    cacheTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const helpfulLinks = data?.generalDetails?.videos || [];

  const handleLinkClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={<IntlMessages id="toolTip.title" />}
      isCrossButton={true}
    >
      <SupportModalWrapper>
        <SupportSection>
          <SectionTitle>
            <IntlMessages id="supprt.modal.title" />
          </SectionTitle>
          <ContactInfo>
            <ContactItem>
              <ContactLabel>
                <IntlMessages id="table.col.email" /> :
              </ContactLabel>
              <ContactLink href="mailto:support@bioplus.live">
                {data?.data?.email || 'support@agmanager.app'}
              </ContactLink>
            </ContactItem>
            <ContactItem>
              <ContactLabel>
                <IntlMessages id="table.col.phone" /> :
              </ContactLabel>
              <ContactLink href={`tel:${data?.data?.phone || '+61384887566'}`}>
                {data?.data?.phone || '+61 384 887 566'}
              </ContactLink>
            </ContactItem>
          </ContactInfo>
        </SupportSection>

        <Divider />

        <HelpfulLinksSection>
          <SectionTitle>
            <IntlMessages id="support.links.title" />
          </SectionTitle>

          {/* Show Loading State */}
          {isLoading && <LoadingText>Loading helpful links...</LoadingText>}

          {/* Show Error State */}
          {isError && <ErrorText>Failed to load links. Please try again later.</ErrorText>}

          {/* Show Links */}
          {!isLoading && !isError && helpfulLinks.length > 0
            ? helpfulLinks.map((link, index) => (
                <LinkContainer key={index} onClick={() => handleLinkClick(link.url)}>
                  <PlayCircle style={{ color: '#4a9eff' }} />
                  <LinkLabel>{link.title}</LinkLabel>
                  <ExternalLinkIcon />
                </LinkContainer>
              ))
            : !isLoading && !isError && <LoadingText>No links available</LoadingText>}
        </HelpfulLinksSection>
      </SupportModalWrapper>
    </Modal>
  );
};

export default SupportModal;
